import React, { useCallback, useReducer } from "react";

import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { noop } from "lodash";
import type { ExtendedKeyboardEvent } from "mousetrap";

import { confirm } from "~/lib/prompts";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isDesktopApp, isMacos } from "~/lib/utils";

import { GlobalKeyboardListener } from "../common";
import BasePanel from "./BasePanel";
import type { Event, MeetingCostSettings, MeetingGuidelinesSettings } from "./CalendarPage";
import type { EventFormResult, EventFormResultEvent } from "./EventForm";
import EventFormPanel from "./EventFormPanel";
import isEventEditable from "./utils/isEventEditable";
import ViewEvent from "./ViewEvent";

import type { EventForm_viewer$key } from "~/__generated__/EventForm_viewer.graphql";

import styles from "./SidePanel.module.css";

export const SHARP_EASE_OUT_CUBIC_BEZIER = [0.42, 0.74, 0.58, 1];

export type SidePanelMode = "closed" | "opened" | "event_info" | "event_form";

export type SidePanelState = {
	mode: SidePanelMode;
	selectedEvent?: Event;
	previouslySelectedEvent?: Event;
	panelWasOpen: boolean;
	editedEvent?: Event;
};

export type SidePanelAction =
	| {
			type: "closed";
	  }
	| {
			type: "opened";
	  }
	| {
			type: "event_info";
			event: Event;
	  }
	| {
			type: "event_form";
			editedEvent?: Event;
	  };

function panelStateReducer(panelState: SidePanelState, panelAction: SidePanelAction): SidePanelState {
	const nextState = {
		panelWasOpen: panelState.mode !== "closed",
		previouslySelectedEvent: panelState.selectedEvent ? panelState.selectedEvent : panelState.previouslySelectedEvent,
		editedEvent: undefined,
	};

	switch (panelAction.type) {
		case "opened": {
			return {
				...nextState,
				mode: "opened",
				selectedEvent: undefined,
				previouslySelectedEvent: undefined,
			};
		}
		case "closed": {
			return {
				...nextState,
				mode: "closed",
				selectedEvent: undefined,
				previouslySelectedEvent: undefined,
			};
		}
		case "event_info": {
			return {
				...nextState,
				mode: "event_info",
				selectedEvent: panelAction.event,
			};
		}
		case "event_form": {
			return {
				...nextState,
				mode: "event_form",
				selectedEvent: panelAction.editedEvent,
				editedEvent: panelAction.editedEvent,
			};
		}
	}
}

export function useSidePanelState(initial: () => SidePanelState) {
	return useReducer(panelStateReducer, null, initial);
}

export type SidePanelNavContextType = {
	navState: SidePanelState;
	navStateDispatch: React.Dispatch<SidePanelAction>;
	week: Date;
	setWeek: (date: Date) => void;
	formHasEdits: boolean;
	setFormHasEdits: (val: boolean) => void;
};

export const SidePanelNavContext = React.createContext<SidePanelNavContextType>({
	navState: { mode: "closed", panelWasOpen: false },
	navStateDispatch: noop,
	week: new Date(),
	setWeek: noop,
	formHasEdits: false,
	setFormHasEdits: noop,
});

export type SidePanelProps = {
	navState: SidePanelState;
	navStateDispatch: React.Dispatch<SidePanelAction>;
	viewer: EventForm_viewer$key;
	onEventSaved: (savedEvent: EventFormResultEvent) => void;
	meetingGuidelinesSettings: MeetingGuidelinesSettings;
	meetingCostSettings: MeetingCostSettings;
	hasAccessToSchedulingMeetingAssistant: boolean;
};

export function closeSidePanel(navState: SidePanelState, navStateDispatch: React.Dispatch<SidePanelAction>) {
	if (navState.mode === "event_form" && navState.previouslySelectedEvent) {
		navStateDispatch({ type: "event_info", event: navState.previouslySelectedEvent });
	} else {
		const action = navState.panelWasOpen ? "opened" : "closed";
		navStateDispatch({ type: action });
		dispatchAnalyticsTrack("Side panel action", { feature: "Calendar", action });
	}
}

export async function checkAndCloseSidePanel(
	formHasEdits: boolean,
	navState: SidePanelState,
	navStateDispatch: React.Dispatch<SidePanelAction>,
) {
	if (formHasEdits) {
		const okayToClose = await confirm("Changes you've made so far will not be saved.", {
			yesText: "Discard",
			noText: "Keep editing",
			simple: true,
			title: "Discard unsaved changes?",
		});

		if (!okayToClose) {
			dispatchAnalyticsTrack("Side panel action", { feature: "Calendar", action: "close prevented" });
			return false;
		}
	}

	closeSidePanel(navState, navStateDispatch);
	return true;
}

export default function SidePanel({
	navState,
	navStateDispatch,
	viewer,
	onEventSaved,
	meetingGuidelinesSettings,
	meetingCostSettings,
	hasAccessToSchedulingMeetingAssistant,
}: SidePanelProps) {
	const [viewEventFetchKey, setViewEventFetchKey] = React.useState(0);
	const { formHasEdits } = React.useContext(SidePanelNavContext);

	const closePanel = useCallback(() => {
		const nextState = navState.mode === "event_form" && navState.previouslySelectedEvent ? "event_info" : "opened";
		dispatchAnalyticsTrack("Side panel action", { feature: "Calendar", action: "close", nextState });
		closeSidePanel(navState, navStateDispatch);
	}, [navState, navStateDispatch]);

	const checkAndClose = useCallback(() => {
		checkAndCloseSidePanel(formHasEdits, navState, navStateDispatch);
	}, [formHasEdits, navState, navStateDispatch]);

	const onFormFinish = useCallback(
		(result: EventFormResult) => {
			switch (result.type) {
				case "cancelled": {
					return checkAndClose();
				}
				case "succeeded": {
					const { event } = result;
					if (!navState.previouslySelectedEvent) {
						// create event - send changes and close the panel
						onEventSaved(event);
						navStateDispatch({ type: navState.panelWasOpen ? "opened" : "closed" });
						return;
					}

					if (navState.previouslySelectedEvent.guid !== event?.eventGuid) {
						// Special case - edited event and got a new guid (means it was "edit this and following") - close the panel
						navStateDispatch({ type: navState.panelWasOpen ? "opened" : "closed" });
						return;
					}

					// edit event - need to refetch and show the updated event
					setViewEventFetchKey(key => key + 1);
					onEventSaved(event);
					navStateDispatch({ type: "event_info", event: navState.previouslySelectedEvent });

					return;
				}
				case "failed":
					return;
				case "deleted": {
					navStateDispatch({ type: navState.panelWasOpen ? "opened" : "closed" });
					return;
				}
			}
		},
		[checkAndClose, navState, navStateDispatch, onEventSaved],
	);

	const onEditEventClicked = useCallback(() => {
		if (navState.selectedEvent) {
			const event = navState.selectedEvent;
			navStateDispatch({ type: "event_form", editedEvent: event });
			dispatchAnalyticsTrack("Event edit clicked", { feature: "Calendar", event });
		}
	}, [navState.selectedEvent, navStateDispatch]);

	const onEditShortcutUsed = useCallback(() => {
		if (!navState.selectedEvent) return;

		const { canEdit } = isEventEditable(navState.selectedEvent);
		if (!canEdit) return;

		const event = navState.selectedEvent;
		navStateDispatch({ type: "event_form", editedEvent: event });

		dispatchAnalyticsTrack("Keyboard shortcut used: edit event", { feature: "Calendar", event });
	}, [navState.selectedEvent, navStateDispatch]);

	const escape = useCallback(
		(event: ExtendedKeyboardEvent) => {
			switch (navState.mode) {
				case "closed":
				case "opened": {
					return;
				}
				case "event_info": {
					dispatchAnalyticsTrack("Keyboard shortcut used: close event details", { feature: "Calendar" });
					event.preventDefault();
					closePanel();
					return;
				}
				case "event_form": {
					dispatchAnalyticsTrack("Keyboard shortcut used: close event form", { feature: "Calendar" });
					event.preventDefault();
					checkAndClose();
					return;
				}
			}
		},
		[closePanel, checkAndClose, navState.mode],
	);

	return (
		<AnimatePresence initial={false}>
			{navState.mode !== "closed" && (
				<motion.div
					className={cx(styles.sidePanel, {
						[styles.eventForm]: navState.mode === "event_form",
						[styles.macosMode]: isDesktopApp && isMacos,
					})}
					key="side-panel"
					layout
					style={{ width: 320 }}
					initial={{ marginRight: -320 }}
					animate={{ marginRight: 0 }}
					exit={{ marginRight: -320 }}
					transition={{ duration: 0.3, ease: SHARP_EASE_OUT_CUBIC_BEZIER }}
				>
					<GlobalKeyboardListener sequence="escape" onEvent={escape} />
					<GlobalKeyboardListener sequence="e" onEvent={onEditShortcutUsed} />
					<BasePanel />
					<AnimatePresence>
						{navState.selectedEvent && (
							<ViewEvent
								onClose={closePanel}
								panelWasOpen={navState.panelWasOpen}
								event={navState.selectedEvent}
								onEditClicked={onEditEventClicked}
								fetchKey={viewEventFetchKey}
								meetingCostSettings={meetingCostSettings}
								hasAccessToSchedulingMeetingAssistant={hasAccessToSchedulingMeetingAssistant}
							/>
						)}
					</AnimatePresence>
					<AnimatePresence>
						{navState.mode === "event_form" && (
							<EventFormPanel
								closePanel={checkAndClose}
								checkAndClose={checkAndClose}
								onFormFinish={onFormFinish}
								panelWasOpen={navState.panelWasOpen}
								viewer={viewer}
								eventToEdit={navState.editedEvent}
								meetingGuidelinesSettings={meetingGuidelinesSettings}
								meetingCostSettings={meetingCostSettings}
								hasAccessToSchedulingMeetingAssistant={hasAccessToSchedulingMeetingAssistant}
							/>
						)}
					</AnimatePresence>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
